import { AlertTriangleIcon, FileTextIcon, RefreshCwIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useRegen } from '@/contexts/regen-context'
import { useSubscription } from '@/contexts/subscription-context'

type Accent = 'viol' | 'pion' | 'turkos'

const ACCENTS = {
  pion: { shadow: 'rgba(178,58,102,0.18)' },
  turkos: { shadow: 'rgba(14,124,102,0.18)' },
  viol: { shadow: 'rgba(109,79,211,0.20)' },
} as const

type Props = {
  accent?: Accent
}

export const StaleBanner = ({ accent = 'viol' }: Props) => {
  const { t } = useTranslation()
  const regen = useRegen()
  const { isReadOnly } = useSubscription()
  const { newDocsCount, shouldOffer } = regen.offer

  const showError = regen.isError && !regen.isRunning

  if (!regen.isRunning && !showError && !shouldOffer) return null

  const a = ACCENTS[accent]

  return (
    <div
      className="flex w-full max-w-3xl flex-wrap items-start gap-4 rounded-[20px] bg-white p-6 sm:flex-nowrap sm:items-center sm:gap-6"
      style={{
        boxShadow: `0 1px 2px rgba(20,33,28,0.03), 0 14px 34px -20px ${a.shadow}`,
      }}
    >
      {/* Ikon */}
      <div className="flex size-7 shrink-0 self-start items-center justify-center">
        {showError ? (
          <AlertTriangleIcon className="text-error" size={26} strokeWidth={2} />
        ) : (
          <RefreshCwIcon
            className={regen.isRunning ? 'animate-spin text-brand-gran' : 'text-brand-gran'}
            size={26}
            strokeWidth={2}
          />
        )}
      </div>

      {/* Innehåll */}
      <div className="flex-1 min-w-0">
        <p className="text-[18px] font-bold leading-snug tracking-tight text-[#14211C]">
          {regen.isRunning
            ? t('homePage.staleBanner.updatingTitle')
            : showError
              ? t('homePage.staleBanner.failedTitle')
              : t('homePage.staleBanner.updateTitle')}
        </p>
        <p className="mt-1 text-[14.5px] leading-relaxed text-[#5B6B65]">
          {regen.isRunning
            ? t('homePage.staleBanner.updatingDescription')
            : showError
              ? t('homePage.staleBanner.failedDescription')
              : t('homePage.staleBanner.updateDescription')}
        </p>

        {!regen.isRunning && !showError && newDocsCount > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex h-[30px] items-center gap-1.5 rounded-full border border-[#E7ECEA] bg-[#F4F6F5] px-3 text-[13px] font-semibold text-[#2C3B35]">
              <FileTextIcon size={13} stroke="#5B6B65" strokeWidth={1.9} />
              {t('homePage.staleBanner.documentsChip')}
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#E2E8E5] px-1 text-[11px] font-bold text-[#2C3B35]">
                {newDocsCount}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* CTA */}
      {/* Regenerating re-runs every generation for the individual, so it needs an
          active subscription. Hidden rather than disabled: a stale banner whose only
          action is unavailable is just noise. */}
      {!regen.isRunning && !isReadOnly && (
        <Button className="w-full shrink-0 sm:w-auto" onClick={regen.regenerate}>
          {t('homePage.staleBanner.updateNow')}
        </Button>
      )}
    </div>
  )
}

export default StaleBanner
